import React, { useEffect, FC, useState } from 'react';
import {
  FormControl,
  Select,
  TextFieldSize,
  TextFieldAppearance,
  AvatarSize,
  Box,
  Text,
  Combobox,
  theme,
} from '@bechtel/gds';
import { HeadingStyled } from './styled';
import { GET_CREW_MEMBERS_WITH_CREW } from '@bechtel/client-gql';
import { find, get } from 'lodash';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { PersonaType, CrewMemberList, SearchCrewInput, CrewMember } from '@bechtel/types';
import { ErrorRetry, useDebounce, useName, useCrewHierachy } from '@bechtel/client-common';
import { CrewSelectionProps } from './types';
import { AvatarRegularStyled } from '../styled';
import { MockForemanId, MockGeneralForemanId, MockSuperintendentId } from '../types';
import { Trans, t } from '@lingui/macro';

const ItemComponent = ({ item }: any) => {
  const { getFullName } = useName();
  const crewNumber = item?.crew?.crewNumber;
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }}>
      <Box mr={{ sm: '400' }}>
        <AvatarRegularStyled
          hasImageUrl={item && item.imageUrl ? true : false}
          size={AvatarSize.MEDIUM}
          src={item && item.imageUrl ? item.imageUrl : undefined}
        />
      </Box>
      <Box flexDirection={{ sm: 'column' }}>
        <HeadingStyled>{getFullName(item.firstName, item.lastName)}</HeadingStyled>
        {crewNumber && item.persona === PersonaType.FOREMAN && (
          <Text size="200" color="grey.1000" transform="capitalize">
            {crewNumber}
          </Text>
        )}
      </Box>
    </Box>
  );
};

const FormControlLabel = ({ label, disabled }) => {
  return (
    <Text family="graphik" color="bechtel.blue.800" size="300" css={disabled ? 'opacity:0.5' : ''}>
      {label}
    </Text>
  );
};

export const CrewSelection: FC<CrewSelectionProps> = ({
  onCrewSelect,
  selectedCrewMembers,
  isSourceSelection,
  moveEntireCrew,
  disableSuperintendent,
  disableGeneralForeman,
}) => {
  const { getFullName } = useName();
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText?.trim(), 500);
  const itemToString = (item) => getFullName(item.firstName, item.lastName);
  const selectedSuperintendent = find(
    selectedCrewMembers,
    (item: CrewMember) => item.persona === PersonaType.SUPERINTENDENT
  );
  const selectedGeneralForeman = find(
    selectedCrewMembers,
    (item: CrewMember) => item.persona === PersonaType.GENERAL_FOREMAN
  );
  const selectedForeman = find(selectedCrewMembers, (item: CrewMember) => item.persona === PersonaType.FOREMAN);

  const [
    getSuperintendentList,
    { data: supData, loading: supLoading, error: supError, refetch: supRefetch, networkStatus: supNetworkStatus },
  ] = useCrewHierachy(true);

  const [
    getGenralForemanList,
    {
      data: generalForemanData,
      error: generalForemanError,
      loading: generalForemanLoading,
      refetch: generalForemanRefetch,
      networkStatus: generalForemanNetworkStatus,
    },
  ] = useCrewHierachy(true);
  const [
    getForemanList,
    {
      data: foremanData,
      error: foremanError,
      loading: foremanLoading,
      refetch: foremanRefetch,
      networkStatus: foremanNetworkStatus,
    },
  ] = useLazyQuery<{ crewMembers: CrewMemberList }, { input: SearchCrewInput }>(GET_CREW_MEMBERS_WITH_CREW, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only', //fetchPolicy: 'network-only' to get the latest value for hasPendingCrewChangeRequest
    nextFetchPolicy: 'cache-first',
  });

  const superintendentList = get(supData, 'crewHierarchy.items', []) as CrewMemberList['items'];
  const generalForemanList = get(generalForemanData, 'crewHierarchy.items', []) as CrewMemberList['items'];
  const foremanList = get(foremanData, 'crewHierarchy.items', []) as CrewMemberList['items'];

  const foremanDisabled =
    foremanList.length === 0 || selectedGeneralForeman?.id === MockGeneralForemanId || moveEntireCrew;

  useEffect(() => {
    if (disableSuperintendent) return;
    getSuperintendentList({
      variables: {
        input: {
          searchKeyword: debouncedSearchText,
        },
        paging: {
          cursor: '1',
          limit: 20,
        },
      },
    });
  }, [debouncedSearchText, getSuperintendentList, disableSuperintendent]);

  useEffect(() => {
    if (
      (selectedSuperintendent && selectedSuperintendent.id === MockSuperintendentId) ||
      disableGeneralForeman ||
      !selectedSuperintendent?.id
    )
      return;
    getGenralForemanList({
      variables: {
        input: {
          superintendentId: selectedSuperintendent?.id,
        },
      },
    });
  }, [selectedSuperintendent, getGenralForemanList, disableGeneralForeman]);

  useEffect(() => {
    if ((selectedGeneralForeman && selectedGeneralForeman.id === MockGeneralForemanId) || moveEntireCrew) return;
    getForemanList({
      variables: {
        input: {
          superintendentId: selectedSuperintendent?.id,
          generalForemanId: selectedGeneralForeman?.id,
        },
      },
    });
  }, [selectedGeneralForeman, getForemanList, moveEntireCrew, selectedSuperintendent]);
  return (
    <Box flexDirection={{ sm: 'column' }}>
      {!supLoading && supError && (
        <ErrorRetry refetch={supRefetch}>
          <Trans>There was a problem loading Superintendents</Trans>
        </ErrorRetry>
      )}
      {!supError && (
        <FormControl
          label={<FormControlLabel label={t`Superintendent`} disabled={disableSuperintendent} />}
          css={{ marginBottom: theme.spacings[600] }}
          data-testid="type-superintendent"
        >
          <Box css={disableSuperintendent ? 'opacity:0.5; pointer-events:none' : ''}>
            <Combobox
              ariaLabel="superintendent"
              size={TextFieldSize.LARGE}
              items={superintendentList}
              appearance={TextFieldAppearance.NORMAL}
              itemString={itemToString}
              itemComponent={ItemComponent}
              selectedComponent={ItemComponent}
              selectedItem={selectedSuperintendent}
              onSelect={async (selectedItem: any) => {
                onCrewSelect(selectedItem, isSourceSelection);
              }}
              term={searchText}
              setTerm={setSearchText}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setSearchText(event.target.value);
              }}
              restorePreviousOnSkip
              loading={supLoading || supNetworkStatus === NetworkStatus.refetch}
              messageOnNoResults={t`No users found`}
            ></Combobox>
          </Box>
        </FormControl>
      )}
      {!generalForemanLoading && generalForemanError && (
        <ErrorRetry refetch={generalForemanRefetch}>
          <Trans>There was a problem loading General Foreman</Trans>
        </ErrorRetry>
      )}
      {!generalForemanError && (
        <FormControl
          label={
            <FormControlLabel
              label={t`General Foreman`}
              disabled={generalForemanList.length === 0 || disableGeneralForeman || !selectedSuperintendent?.id}
            />
          }
          css={{ marginBottom: theme.spacings[600] }}
        >
          <Select
            size={TextFieldSize.LARGE}
            appearance={TextFieldAppearance.NORMAL}
            items={generalForemanList}
            itemComponent={ItemComponent}
            disabled={generalForemanList.length === 0 || disableGeneralForeman}
            selectedComponent={ItemComponent}
            selectedItem={selectedGeneralForeman?.id !== MockGeneralForemanId ? selectedGeneralForeman : null}
            onSelectedItemChange={async (changes: any) => {
              onCrewSelect(changes.selectedItem, isSourceSelection);
            }}
            loading={generalForemanLoading || generalForemanNetworkStatus === NetworkStatus.refetch}
          ></Select>
        </FormControl>
      )}
      {!foremanLoading && foremanError && (
        <ErrorRetry refetch={foremanRefetch}>
          <Trans>There was a problem loading Foreman</Trans>
        </ErrorRetry>
      )}
      {!foremanError && (
        <FormControl
          label={
            <FormControlLabel
              label={t`Foreman`}
              disabled={foremanDisabled || !selectedGeneralForeman?.id || !selectedSuperintendent?.id}
            />
          }
          data-testid="type-Foreman"
        >
          <Select
            size={TextFieldSize.LARGE}
            appearance={TextFieldAppearance.NORMAL}
            items={foremanList}
            itemComponent={ItemComponent}
            selectedItem={selectedForeman?.id !== MockForemanId ? selectedForeman : null}
            disabled={foremanDisabled}
            selectedComponent={ItemComponent}
            onSelectedItemChange={async (changes: any) => {
              onCrewSelect(changes.selectedItem, isSourceSelection);
            }}
            loading={foremanLoading || foremanNetworkStatus === NetworkStatus.refetch}
          ></Select>
        </FormControl>
      )}
    </Box>
  );
};
